import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { bool, func, string } from 'prop-types';
import moment from 'moment';
import {
  TextFieldLabel,
  DatePicker,
  Select,
  Spinner,
} from 'ui-component-library';
import * as yup from 'yup';

import InfoSection from '../../card-shared-components/card-info-section';
import LabelValue from '../../card-shared-components/card-label-value';
import {
  DateLabelContainer,
  DateLabel,
  Label,
  Text,
  DateTimeContainer,
  LateBadgeContainer,
  LocationRemarks,
  NoRemarks,
  HeaderButtonsGroup,
  HeaderButton,
  EditContainer,
  ControlContainer,
  LoadingContainer,
} from './shipment-stop-section.style';
import calendarIcon from '../../../../../assets/icon-calendar.svg';
import { BADGE_TYPES, MODE_TYPES, STOP_TYPES } from '../../../../../constants';
import theme from '../../../../../theme';
import Badge from '../../badge';
import shipmentStopShape from '../../../../../shapes/shipment-stop';
import { formatPhoneNumber } from '../../../../../utils/formatters';
import editIcon from '../../../../../assets/icon-pencil.svg';
import TimeInput from '../../time-input';
import {
  getDate, getTimeString, parseDate, showErrorMessage, showSuccessMessage, logger,
} from '../../../../../utils/common';
import { createOption } from '../../../../../utils/mappers';
import { updateShipmentStop } from '../../../../../utils/api/load';
import ImageIcon from '../../image-icon/image-icon';

export const schema = yup.object().shape({
  checkInTime: yup.string().required(),
  checkInDate: yup.string().required(),
  lateReason: yup.string().when('$requiresLateReason', {
    is: true,
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  checkOutDate: yup.string().optional().nullable(),
  checkOutTime: yup.string().when('checkOutDate', {
    is: (val) => (!!val),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
});

/**
 * The Stop Section Component
 * @param {object} params - Object containing props
 * @param {object} stop - stop data to show
 * @param {string} stopType - type of stop to display
 * @param {string} testId - testing id
 * @param {bool} locationRemarksShown - Specifies if the location remarks are shown
 * @param {string} params.loadId - Specifies the loadId associated to this data
 * @param {string} params.salesOrderBk - Specifies the sales order bk associated to this data
 * @param {string} params.status - Specifies the load status associated to this data
 * @param {string} params.destinationLocationNumber - Specifies the location number of this routes destination
 * @param {func} params.refetchData - Function used to refetch the data relevant to this view
 * @param {boolean} params.canEdit - Specifies if this stop section can be edited
 * @param {string} params.mode - Mode type
 * @return {element}
 */
const StopSection = ({
  stop,
  stopType,
  testId,
  locationRemarksShown,
  loadId,
  salesOrderBk,
  status,
  destinationLocationNumber,
  refetchData,
  canEdit,
  mode,
}) => {
  // eslint-disable-next-line no-unused-vars

  // State hooks
  const [editing, setEditing] = useState(false);
  const [checkInTime, setCheckInTime] = useState();
  const [checkOutTime, setCheckOutTime] = useState();
  const [checkInDate, setCheckInDate] = useState();
  const [checkOutDate, setCheckOutDate] = useState();
  const [lateReason, setLateReason] = useState(null);
  const [loading, setLoading] = useState(false);

  // Redux Hooks
  const metadata = useSelector((state) => state.metadata);

  const requiresLateReason = (stop?.appointmentStart && !!checkInDate && !!checkInTime)
    && (moment(stop.appointmentStart).utc().isBefore(moment(parseDate(checkInDate, checkInTime)).utc()));

  /**
   * Helper function to format addresses
   * @param {object} address - Address object from API
   * @return {string}
   */
  const formatAddress = (address) => `${address?.address1}, ${address?.city}, ${address?.state} ${address?.postalCode}`;

  /**
   * Formats a ISO string as UTC
   * @param {string} date - Date string
   * @return {string} - Utc formatted string
   */
  const formatUtc = (date) => (date ? moment(date).utc().format('ddd, MM/DD  HH:mm') : '');

  /**
   * Helper function to format the header of a stop section
   * @return {string}
   */
  const formatHeader = () => {
    if (stop.isShipper) {
      return `Shipper: ${formatAddress(stop.address)}`;
    }
    if (stop.isConsignee) {
      return `Consignee: ${formatAddress(stop.address)}`;
    }
    return `Stop: ${formatAddress(stop.address)}`;
  };

  /**
   * Helper function to format the LoadNumber label
   * @return {string}
   */
  const formatLoadNumberLabel = () => {
    switch (stopType) {
      case STOP_TYPES.origin:
        return 'Pickup #';
      case STOP_TYPES.destination:
        return 'Drop Off #';
      case STOP_TYPES.inBetween:
        return 'Reference #';
      default:
        return formatAddress(stop.address);
    }
  };

  /**
   * Helper function to format the actual time(s)
   * labels
   * @return {string}
   */
  const formatActualTimeLabel = () => {
    switch (stopType) {
      case STOP_TYPES.origin:
        return 'Actual Pickup';
      case STOP_TYPES.destination:
        return 'Actual Drop Off';
      case STOP_TYPES.inBetween:
        return 'Actual Times';
      default:
        return 'Actual Times';
    }
  };

  /**
   * Renders the appotinment times based on a start.
   * and end time
   * @param {string} start - ISO datetime start string
   * @param {*} end - ISO datetime end string.
   * @return {element} - Appointment time date nodes.
   */
  const renderAppointmentTimeDate = (start, end, noTime) => {
    if (!start || start === '') {
      return '';
    }
    let times;
    const day = moment(start).utc().format('ddd, MM/DD');
    if (noTime) {
      return (
        <Text>{day}</Text>
      );
    }
    if (!end) {
      times = moment(start).utc().format('HH:mm');
    } else {
      times = `${moment(start).utc().format('HH:mm')} - ${moment(end).utc().format('HH:mm')}`;
    }
    return (
      <>
        <Text>{day}</Text>
        <br />
        <Text>{times}</Text>
      </>
    );
  };

  /**
   * Renders the correct message based on remark
   * @param {string} remark - Remark to show
   * @returns {element}
   */
  const renderLocationRemark = (remark) => {
    if (!remark || remark.length === 0) {
      return (
        <NoRemarks>
          There are no notes for this stop.
        </NoRemarks>
      );
    }
    return (
      <LocationRemarks>
        {remark}
      </LocationRemarks>
    );
  };

  // Don't render anything if we don't have data
  if (!stop) {
    return null;
  }

  if (locationRemarksShown) {
    return (
      <InfoSection
        header={formatHeader()}
        testId={testId}
        backgroundColor={stopType === STOP_TYPES.inBetween ? theme.palette.lightGray[50] : ''}
      >
        {renderLocationRemark(stop.notes)}
      </InfoSection>
    );
  }

  /**
   * Handles submitting a payload when editing this shipment stop section
   * @return {void}
   */
  const handleSubmit = () => {
    const payload = {
      loadId,
      checkIn: parseDate(checkInDate, checkInTime),
      checkOut: (!!checkOutDate && !!checkOutTime) ? parseDate(checkOutDate, checkOutTime) : undefined,
      lateReason: requiresLateReason ? lateReason?.value : undefined,
      locationNumber: stop.locationNumber,
      originalStatus: status,
      thisStop: stop,
      destinationLocationNumber,
    };

    setLoading(true);
    updateShipmentStop(payload, salesOrderBk).then((res) => {
      setEditing(false);
      const updatedStatus = res?.data?.model;
      refetchData({ tlStatus: updatedStatus });
      showSuccessMessage(`Shipment stop updating on load #${salesOrderBk}. This information may take a moment to update.`);
    }).catch((error) => {
      logger.error(error);
      showErrorMessage('Something went wrong. The service is currently unavailable. Please try again later.');
    }).finally(() => {
      setLoading(false);
    });
  };

  /**
   * Toggles editing stop for this shipment stop
   * @return {void}
   */
  const toggleEditing = () => {
    setCheckInTime(getTimeString(stop.actualCheckIn));
    setCheckOutTime(getTimeString(stop.actualCheckOut));
    setCheckInDate(getDate(stop.actualCheckIn));
    setCheckOutDate(getDate(stop.actualCheckOut));
    if (stop.lateReason !== 'None') {
      setLateReason(createOption(stop.lateReason));
    }
    setEditing(true);
  };

  /**
   * Renders the header content for the shipment stop section
   * @param {boolean} disableSave - Specifies if the save button should be shown
   * @returns {node}
   */
  const renderShipmentStopsHeaderContent = (disableSave) => (!editing ? (
    <ImageIcon
      height={15}
      width={15}
      src={editIcon}
      onClick={toggleEditing}
      altString="Edit Icon"
      offCenter
    />
  ) : (
    <HeaderButtonsGroup>
      <HeaderButton onClick={() => setEditing(false)} type="cancel" text="Cancel" />
      {!disableSave && <HeaderButton onClick={handleSubmit} type="submit" text="Save" />}
    </HeaderButtonsGroup>
  ));

  /**
   * Function handling rendering the check in check out form for a shipment stop
   * @return {node}
   */
  const renderCheckInCheckOutForm = () => {
    const canEnterCheckOut = checkInTime !== null && checkInTime !== '' && checkInDate !== null;
    const lateReasons = metadata.data.lateReasons.filter((reason) => reason !== 'None');
    // Construct late reason options
    const lateReasonOptions = lateReasons?.map(createOption) || [];
    return (
      <div>
        <DateLabel marginBottom="40px">{formatActualTimeLabel()}</DateLabel>
        <InfoSection numCols={3} removePadding>
          <ControlContainer>
            <TextFieldLabel text="*Check-In Date" />
            <DatePicker
              selectedDate={checkInDate}
              onValueChange={(value) => setCheckInDate(value)}
              placeholder="Choose Date"
            />
          </ControlContainer>
          <ControlContainer>
            <TextFieldLabel text="*Check-In Time" />
            <TimeInput
              value={checkInTime || ''}
              onValueChanged={(event) => setCheckInTime(event.target.value)}
            />
          </ControlContainer>
          {requiresLateReason && (
            <ControlContainer>
              <TextFieldLabel text="*Late Reason" />
              <Select
                selectedValue={lateReason}
                options={lateReasonOptions}
                onValueChange={(event) => setLateReason(event)}
                placeholder="Select..."
              />
            </ControlContainer>
          )}
          {!requiresLateReason && <div />}
          {canEnterCheckOut && (
            <>
              <ControlContainer>
                <TextFieldLabel text="Check-Out Date" />
                <DatePicker
                  selectedDate={checkOutDate}
                  onValueChange={(value) => setCheckOutDate(value)}
                  placeholder="Choose Date"
                />
              </ControlContainer>
              <ControlContainer>
                <TextFieldLabel text="Check-Out Time" />
                <TimeInput
                  value={checkOutTime || ''}
                  onValueChanged={(event) => setCheckOutTime(event.target.value)}
                />
              </ControlContainer>
            </>
          )}
        </InfoSection>
      </div>
    );
  };

  /**
   * Function handling rendering the middle appointment row for TL
   * @return {node}
   */
  const renderTLAppointmentRow = () => (
    <>
      <div>
        <DateLabelContainer>
          <ImageIcon src={calendarIcon} height={15} width={15} altString="calendar" />
          <DateLabel>{stop.appointmentType}</DateLabel>
          {stop.isLate && (
            <LateBadgeContainer data-testid="late-badge">
              <Badge type={BADGE_TYPES.late} />
            </LateBadgeContainer>
          )}
        </DateLabelContainer>
        {renderAppointmentTimeDate(stop.appointmentStart, stop.appointmentEnd)}
      </div>
      {!editing && (
        <div>
          <DateLabelContainer>
            <ImageIcon src={calendarIcon} height={15} width={15} altString="calendar" />
            <DateLabel>{formatActualTimeLabel()}</DateLabel>
          </DateLabelContainer>
          <>
            <DateTimeContainer>
              <Text>In:</Text>
              <Text>{`${formatUtc(stop.actualCheckIn)}`}</Text>
            </DateTimeContainer>
            <DateTimeContainer>
              <Text>Out:</Text>
              <Text>{`${formatUtc(stop.actualCheckOut)}`}</Text>
            </DateTimeContainer>
          </>
        </div>
      )}
      {!editing && stop.lateReason && stop.lateReason !== 'None' && <LabelValue label="Late Reason" value={stop.lateReason} options={{ stacked: true }} />}
      <EditContainer>
        {editing && renderCheckInCheckOutForm()}
      </EditContainer>
    </>
  );

  /**
   * Function handling rendering the middle appointment row for LTL
   * @return {node}
   */
  // TODO: Update with LTL scheduled, updated, actual appointments,
  //       & appointment types once BE endpoint becomes available.
  const renderLTLAppointmentRow = () => (
    <>
      <div>
        <DateLabelContainer>
          <ImageIcon src={calendarIcon} height={15} width={15} altString="calendar" />
          <DateLabel>{`Original Est. ${stop.isConsignee ? 'Delivery' : 'Pickup'}`}</DateLabel>
        </DateLabelContainer>
        {renderAppointmentTimeDate(stop.appointmentStart, stop.appointmentEnd)}
      </div>
      <div>
        <DateLabelContainer>
          <ImageIcon src={calendarIcon} height={15} width={15} altString="calendar" />
          <DateLabel>{`Updated Est. ${stop.isConsignee ? 'Delivery' : 'Pickup'}`}</DateLabel>
        </DateLabelContainer>
        {renderAppointmentTimeDate(stop.appointmentStart, stop.appointmentEnd)}
      </div>
      <div>
        <DateLabelContainer>
          <ImageIcon src={calendarIcon} height={15} width={15} altString="calendar" />
          <DateLabel>{stop.isConsignee ? 'Actual Delivery' : 'Actual Pickup'}</DateLabel>
        </DateLabelContainer>
        {renderAppointmentTimeDate(stop.actualCheckIn)}
      </div>
    </>
  );

  /**
  * Checks if the form is valid
  * @return {boolean}
  */
  const checkValid = () => {
    const payload = {
      checkInDate,
      checkOutDate,
      checkInTime,
      checkOutTime,
      lateReason: requiresLateReason ? lateReason?.value : undefined,
    };
    const valid = schema.isValidSync(payload, { context: { requiresLateReason } });
    return valid;
  };

  // Check to see if form entry is valid
  const valid = checkValid();

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }
  return (
    <InfoSection
      header={formatHeader()}
      testId={testId}
      removePadding
      backgroundColor={stopType === STOP_TYPES.inBetween ? theme.palette.lightGray[50] : ''}
      headerContent={canEdit ? renderShipmentStopsHeaderContent(!valid) : null}
    >
      <InfoSection numCols={3} outlineBottom>
        <LabelValue label={formatLoadNumberLabel()} value={stop.locationNumber} options={{ stacked: true }} />
        <LabelValue label="Contact Name" value={stop.contactName} options={{ stacked: true }} />
        <LabelValue label="Contact Phone" value={formatPhoneNumber(stop.contactPhoneNumber)} options={{ stacked: true }} />
      </InfoSection>
      <InfoSection numCols={3} outlineBottom>
        {mode === MODE_TYPES.truckload ? renderTLAppointmentRow() : renderLTLAppointmentRow()}
      </InfoSection>
      <InfoSection>
        <Label>Location Accessorials</Label>
        {stop.locationAccessorials?.map((accessorial, i) => <Text key={i}>{accessorial}</Text>)}
      </InfoSection>
    </InfoSection>
  );
};

StopSection.propTypes = {
  stop: shipmentStopShape.isRequired,
  stopType: string,
  testId: string,
  locationRemarksShown: bool,
  loadId: string.isRequired,
  salesOrderBk: string.isRequired,
  status: string.isRequired,
  destinationLocationNumber: string.isRequired,
  refetchData: func,
  canEdit: bool,
  mode: string.isRequired,
};

StopSection.defaultProps = {
  stopType: STOP_TYPES.origin,
  testId: null,
  locationRemarksShown: false,
  refetchData: () => { },
  canEdit: false,
};

export default StopSection;
